import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, FolderIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { DriveTreeNode } from '@/lib/drive/driveClient';

interface DriveFolderTreeProps {
  tree: DriveTreeNode[];
  selectedPath: string;
  onSelect: (path: string) => void;
  /** Drag-and-drop move: a file row dropped on a tree node moves into that folder. */
  onDropOnFolder?: (folderPath: string) => void;
}

/**
 * The left-hand folder tree over the drive's flat rows. Expansion is the only
 * local state — the tree itself derives from the client's index on render.
 */
export function DriveFolderTree({ tree, selectedPath, onSelect, onDropOnFolder }: DriveFolderTreeProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['/']));

  const toggle = useCallback((path: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const root: DriveTreeNode = useMemo(
    () => ({ id: '__root__', name: t('drive.myFiles'), path: '/', children: tree }),
    [tree, t],
  );

  const renderNode = (node: DriveTreeNode, depth: number) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded.has(node.path);
    const isSelected = selectedPath === node.path;
    return (
      <li key={node.id} role="none">
        {/* Not a <button>: the chevron toggle inside is a button too, and
            interactive elements must not nest — the row keeps treeitem
            semantics on a div with Enter/Space handling instead. */}
        <div
          role="treeitem"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onSelect(node.path);
            }
          }}
          aria-selected={isSelected}
          aria-expanded={hasChildren ? isExpanded : undefined}
          onClick={() => onSelect(node.path)}
          onDragOver={(event) => {
            if (event.dataTransfer.types.includes('application/x-sak-entry')) {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
            }
          }}
          onDrop={(event) => {
            if (!event.dataTransfer.types.includes('application/x-sak-entry')) return;
            event.preventDefault();
            event.stopPropagation();
            onDropOnFolder?.(node.path);
          }}
          className={clsx(
            'flex w-full flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md py-1.5 pe-2 text-sm transition-colors',
            isSelected ? 'bg-active font-medium text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
          style={{ paddingInlineStart: 8 + depth * 14 }}
        >
          {hasChildren ? (
            <button
              type="button"
              aria-label={node.name}
              onClick={(event) => {
                event.stopPropagation();
                toggle(node.path);
              }}
              className="flex-shrink-0 rounded-sm p-0.5 hover:text-foreground"
            >
              <ChevronRightIcon
                className={clsx('h-3.5 w-3.5 transition-transform', isExpanded && 'rotate-90')}
              />
            </button>
          ) : (
            <span className="w-[22px] flex-shrink-0" />
          )}
          <FolderIcon className="h-4 w-4 flex-shrink-0 text-accent" />
          <span className="truncate">{node.name}</span>
        </div>
        {hasChildren && isExpanded && (
          <ul role="group">{node.children.map((child) => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <nav
      aria-label={t('drive.myFiles')}
      className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden rounded-lg border border-border-color bg-card p-2"
      data-tour-id="folder-tree"
    >
      <ul role="tree">{renderNode(root, 0)}</ul>
    </nav>
  );
}
